/** Usernames and guest names (docs/RUSH_HOUR_LAUNCH_PLAN.md §2, §5). Pure: the server adds the blocked-names file.
 * Rules: 3–16 characters, letters, digits, "_" and "-"; unique ignoring case; no reserved or blocked names (compared
 * after normalising look-alikes); nothing that imitates a domain (a known top-level domain after a separator, or
 * "www"); no profanity (profanity.txt, from LDNOOBW, CC BY 4.0). */

export type NameProblem = 'format' | 'reserved' | 'domain' | 'taken';

const FORMAT = /^[A-Za-z0-9_-]{3,16}$/;
const LOOK_ALIKES: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '@': 'a', $: 's' };

/** Lower case, separators removed, look-alike digits mapped to letters, repeated letters collapsed. */
export function normaliseName(name: string): string {
  return name.toLowerCase().replace(/[_-]/g, '').replace(/[013457@$8]/g, char => LOOK_ALIKES[char] ?? char).replace(/(.)\1+/g, '$1');
}

/** A name's look-alike skeleton, stricter than normaliseName: also "rn" reads as "m" and "l" as "i" (they look alike in
 * many fonts), so "ahrned_aItaai" and "4hmedaltaai" share the skeleton of "ahmedaltaai". */
export function nameSkeleton(name: string): string {
  return normaliseName(name).replace(/rn/g, 'm').replace(/l/g, 'i').replace(/vv/g, 'w').replace(/(.)\1+/g, '$1');
}
/** Does the name contain any of these protected names, in any look-alike spelling or with anything around it? */
export function containsProtected(name: string, protectedNames: readonly string[]): boolean {
  const skeleton = nameSkeleton(name);
  return protectedNames.some(protectedName => skeleton.includes(nameSkeleton(protectedName)));
}

/** Common generic and country-code top-level domains (the country codes come from the flag list at runtime). */
const TLDS = ['com', 'net', 'org', 'info', 'biz', 'xyz', 'online', 'site', 'shop', 'store', 'app', 'dev', 'io', 'ai', 'co', 'me', 'tv', 'gg', 'ly', 'fm', 'cc', 'tech', 'live', 'club', 'blog', 'page', 'link', 'top', 'pro', 'eu', 'gov', 'edu', 'mil', 'int', 'name', 'mobi', 'asia', 'tel', 'cloud', 'digital', 'world', 'today', 'space', 'website', 'email', 'one', 'fun', 'games', 'game', 'social', 'media', 'news', 'art', 'design', 'studio', 'agency', 'network', 'company', 'solutions', 'services', 'center', 'zone', 'life', 'love', 'xxx', 'porn', 'sex'];

export type BlockList = { exact: Set<string>; contains: string[] };

/** Parses blocked-usernames.txt: "=name" exact, "~name" or "name" contains; "#" comments. Entries are normalised. */
export function parseBlockList(text: string): BlockList {
  const exact = new Set<string>(), contains: string[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    if (line.startsWith('=')) exact.add(normaliseName(line.slice(1)));
    else contains.push(normaliseName(line.replace(/^~/, '')));
  }
  return { exact, contains: contains.filter(Boolean) };
}

/** Looks like a domain: "name_com", "name-net", "namecom" ending in a TLD after a separator, or starting with "www". */
export function looksLikeDomain(name: string, countryCodes: readonly string[] = []): boolean {
  const lower = name.toLowerCase();
  if (/^www/.test(lower.replace(/[_-]/g, ''))) return true;
  const parts = lower.split(/[_-]/).filter(Boolean);
  const last = parts.length > 1 ? parts[parts.length - 1] : null;
  if (!last) return false;
  return TLDS.includes(last) || countryCodes.some(code => code.toLowerCase() === last);
}

/** Profanity (profanity.txt), in three tiers (owner: slurs never, in any spelling; ordinary names always fine).
 * Every check reads look-alike digits as letters ("n1gg3r"), ignores separators ("n_i_g_g_e_r") and counts stretched
 * letters once ("niiiiger", "nigggger" and "niger" are the same word).
 * 1. SLURS: refused anywhere in the name.
 * 2. Every list word (other languages: 4+ letters): refused as a whole word of the name ("Ass_Kicker", "BigDick", "Sh1tHead"), but not
 *    inside an ordinary word ("Class", "Titan", "Stefan"). Words are split at _, -, digits and camelCase.
 * 3. English list words of 5+ letters: refused anywhere ("xxboobsxx"), except inside a few ordinary words (ORDINARY).
 * 4. Run-together names: refused when the name reads entirely as English list words and common filler words
 *    ("bigdick" = big + dick, "asskicker" = ass + kicker, "xxdickxx"). Ordinary words don't split that way: "Hancock"
 *    (han + cock), "Titan" (tit + an) and "Dickson" (dick + son) have a leftover that isn't a filler word. */
export type Profanity = { words: Set<string>; collapsed: Set<string>; long: string[]; english: Set<string> };
const collapse = (word: string) => word.replace(/(.)\1+/g, '$1');
/** Slurs and the worst words, as their collapsed forms. Chosen so they don't sit inside ordinary names. */
const SLURS = ['niger', 'niga', 'nigr', 'fuck', 'fuk', 'cunt', 'kunt', 'fagot', 'whore', 'hitler', 'kike', 'chink', 'wetback', 'tranny', 'retard', 'rapist', 'pedophil', 'paedo', 'molest', 'nazi'];
/** Ordinary words that contain a slur or a long list word, removed before tiers 1 and 3 ("Nigeria", "Therapist"). */
const ORDINARY = ['nigeria', 'montenegro', 'therapist', 'pussycat', 'pussywillow', 'fukuoka', 'fukushima', 'nazir', 'nazira', 'chinkapin', 'scrapist', 'grapist', 'bobsled', 'bobsleigh', 'kunta'];
export function parseProfanity(text: string): Profanity {
  const words = new Set<string>(), collapsed = new Set<string>(), long = new Set<string>(), englishWords = new Set<string>();
  let english = false;
  for (const raw of text.split('\n')) {
    const word = raw.trim().toLowerCase();
    if (!word || word.startsWith('#')) continue;
    if (word === '[en]' || word === '[other]') { english = word === '[en]'; continue; }
    // Three-letter words from other languages are ordinary English words ("fan"); the slurs are tier 1 anyway.
    if (!english && word.length < 4) continue;
    words.add(word);
    if (collapse(word).length >= 3) collapsed.add(collapse(word)); // "ass" → "as" would hit "Fast_As_Light"
    if (english) englishWords.add(word);
    if (english && collapse(word).length >= 5) long.add(collapse(word)); // shorter collapsed forms hit ordinary words ("ecchi" → "echi" in Czechia)
  }
  return { words, collapsed, long: [...long], english: englishWords };
}
const LETTERS = (text: string) => text.replace(/[013457@$8]/g, char => LOOK_ALIKES[char] ?? char);
/** The whole name and its words (split at _, -, digits and camelCase), as plain lower-case letters. */
export function nameWords(name: string): string[] {
  const mapped = LETTERS(name);
  const parts = [mapped, ...mapped.split(/[^A-Za-z]+/), ...mapped.split(/[^A-Za-z]+|(?<=[a-z])(?=[A-Z])/)];
  return [...new Set(parts.map(part => part.replace(/[^A-Za-z]/g, '').toLowerCase()).filter(Boolean))];
}
/** Words that pad out an insult in a run-together name ("big", "my", "kicker", "xx"). Deliberately not "son", "an", "ton"
 * or other endings of ordinary names. */
const FILLERS = new Set(['big', 'small', 'little', 'lil', 'huge', 'giant', 'mega', 'super', 'fat', 'hot', 'sexy', 'wet', 'hard', 'long', 'old', 'dirty', 'stinky', 'smelly', 'ugly', 'dumb',
  'my', 'ur', 'your', 'yo', 'the', 'mr', 'mrs', 'miss', 'lady', 'master', 'king', 'queen', 'lord', 'boss', 'man', 'men', 'boy', 'boys', 'girl', 'guy', 'dude', 'bro', 'kid',
  'lover', 'love', 'eater', 'eat', 'sucker', 'suck', 'licker', 'lick', 'kicker', 'head', 'face', 'hole', 'wipe', 'bag', 'wad', 'muncher', 'slayer', 'hunter', 'rider',
  'monster', 'god', 'daddy', 'mommy', 'mama', 'papa', 'pro', 'real', 'iam', 'x', 'xx', 'xxx']);
// (No single letters or endings like "s", "er", "es": chained, they spell ordinary words — "Sussex" = s + u + s + sex.)
/** Can `text` be read entirely as list words and fillers, with at least one list word? */
function readsAsInsult(text: string, bad: (piece: string) => boolean): boolean {
  const memo = new Map<string, boolean>();
  const go = (from: number, seenBad: boolean): boolean => {
    if (from === text.length) return seenBad;
    const key = `${from}|${seenBad}`;
    if (memo.has(key)) return memo.get(key)!;
    let ok = false;
    for (let to = from + 1; to <= text.length && !ok; to++) {
      const piece = text.slice(from, to);
      if (bad(piece)) ok = go(to, true);
      if (!ok && FILLERS.has(piece)) ok = go(to, seenBad);
    }
    memo.set(key, ok);
    return ok;
  };
  return go(0, false);
}
export function isProfane(name: string, profanity: Profanity): boolean {
  let whole = collapse(LETTERS(name).toLowerCase().replace(/[^a-z]/g, ''));
  for (const word of ORDINARY) whole = whole.split(collapse(word)).join('|');
  if (SLURS.some(slur => whole.includes(slur))) return true;
  if (nameWords(name).some(word => profanity.words.has(word) || profanity.collapsed.has(collapse(word)))) return true;
  if (profanity.long.some(word => whole.includes(word))) return true;
  // Run-together: as typed ("asskicker"), and with stretched letters counted once ("biiigdiiick"); a plural counts too
  // ("bigdicks"). Each stretch between ordinary words is read on its own ("Therapist" stays fine).
  const listed = (piece: string) => piece.length >= 3 && (profanity.english.has(piece) || (piece.length >= 4 && piece.endsWith('s') && profanity.english.has(piece.slice(0, -1))));
  const plain = LETTERS(name).toLowerCase().replace(/[^a-z]/g, '');
  const stretches = (text: string) => ORDINARY.reduce((parts, word) => parts.flatMap(part => part.split(word)), [text]).filter(Boolean);
  return stretches(plain).some(part => readsAsInsult(part, listed)) || stretches(collapse(plain)).some(part => readsAsInsult(part, listed));
}

/** Format and reserved/blocked/domain/profanity checks (uniqueness is the database's job). */
export function checkUsername(name: string, blocked: BlockList, countryCodes: readonly string[] = [], profanity?: Profanity): { ok: true; lower: string } | { ok: false; reason: NameProblem } {
  if (!FORMAT.test(name)) return { ok: false, reason: 'format' };
  const normal = normaliseName(name);
  if (blocked.exact.has(normal) || blocked.contains.some(word => normal.includes(word))) return { ok: false, reason: 'reserved' };
  if (profanity && isProfane(name, profanity)) return { ok: false, reason: 'reserved' };
  if (looksLikeDomain(name, countryCodes)) return { ok: false, reason: 'domain' };
  return { ok: true, lower: name.toLowerCase() };
}

export const NAME_PROBLEM_TEXT: Record<NameProblem, string> = {
  format: '3–16 characters: letters, numbers, _ and -.',
  reserved: "That name isn't available.",
  domain: "Names can't look like a website address.",
  taken: 'Someone already has that name.',
};

// Guest names, Reddit style: Adjective_Noun_12345678. Funny, never crude ("Spunky" is out: British slang). createGuest also
// skips any pair the profanity check refuses.
export const GUEST_ADJECTIVES = ['Brave', 'Swift', 'Sneaky', 'Wiggly', 'Mighty', 'Sleepy', 'Fearless', 'Speedy', 'Bold', 'Clumsy', 'Dizzy', 'Eager', 'Fancy', 'Giddy', 'Heroic', 'Jolly', 'Lucky', 'Nimble', 'Plucky', 'Quirky', 'Rapid', 'Sassy', 'Turbo', 'Valiant', 'Wobbly', 'Zesty', 'Bouncy', 'Cheeky', 'Daring', 'Frisky', 'Gallant', 'Hasty', 'Peppy', 'Rowdy', 'Tiny', 'Witty', 'Zippy', 'Noble', 'Dashing'];
export const GUEST_NOUNS = ['Tadpole', 'Spartan', 'Swimmer', 'Hoplite', 'Wiggler', 'Sprinter', 'Lancer', 'Commuter', 'Racer', 'Dasher', 'Paddler', 'Rookie', 'Champion', 'Comet', 'Rocket', 'Torpedo', 'Noodle', 'Squiggle', 'Bolt', 'Dart', 'Arrow', 'Spear', 'Shield', 'Helmet', 'Legend', 'Hero', 'Rebel', 'Ranger', 'Pilot', 'Diver'];

/** A guest name from three random numbers in [0, 1). */
export function guestNameFrom(a: number, b: number, digits: number): string {
  const adjective = GUEST_ADJECTIVES[Math.floor(a * GUEST_ADJECTIVES.length) % GUEST_ADJECTIVES.length];
  const noun = GUEST_NOUNS[Math.floor(b * GUEST_NOUNS.length) % GUEST_NOUNS.length];
  return `${adjective}_${noun}_${String(Math.floor(digits * 90_000_000) + 10_000_000)}`;
}
